import styled from 'styled-components';
import {
  FloatingPortal,
  FloatingOverlay,
  FloatingFocusManager,
  useInteractions,
  useRole,
  useDismiss,
  useFloating,
  useId,
} from '@floating-ui/react';
import React, { ReactElement } from 'react';

import { borderRadius, palette } from '../theme';
import { modularScale, pxToRem } from '../utils';

const Overlay = styled(FloatingOverlay)`
  display: grid;
  place-items: center;
  background-color: rgba(0, 0, 0, 0.2);
  z-index: 100;
  padding: ${modularScale(1)};
`;

const Content = styled.div`
  position: relative;
  background-color: ${palette.brand.white};
  border-radius: ${pxToRem(borderRadius)};
  outline: none;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: ${pxToRem(560)};
  max-height: 80vh;
  z-index: 101;
`;

const InnerContent = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const Delete = styled.button`
  all: unset;
  z-index: 2;
  cursor: pointer;
  position: absolute;
  top: ${modularScale(-2)};
  right: ${modularScale()};
  width: ${modularScale(1)};
  height: ${modularScale(1)};

  &::before,
  &::after {
    content: '';
    position: absolute;
    height: 21px;
    width: 2px;
    top: 0;
    left: 50%;
    background-color: ${palette.grayscale.shade800};
    border-radius: 1px;
  }

  &::before {
    transform: rotate(45deg);
  }
  &::after {
    transform: rotate(-45deg);
  }
`;

const Header = styled.div`
  background-color: ${palette.brand.yellow};
  padding: ${modularScale()} ${modularScale(1)} ${modularScale()};
  border-radius: 12px 12px 0 0;
`;

export interface IModalProps {
  children: React.ReactNode;
  onClose?: () => void;
  headerContent?: React.ReactNode;
  dismissable?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * This component functions as a pop-up window, and can have an optional header. This can be added with the `headerContent` prop.
 *
 * ```ts
 *
 * import { Modal } from '@ottatech/design'
 *
 * ```
 */
export function Modal({
  children,
  onClose,
  headerContent,
  dismissable = true,
  className,
  style,
}: IModalProps): ReactElement {
  const { floating, context } = useFloating({
    open: true,
    onOpenChange(value) {
      if (!value) {
        onClose?.();
      }
    },
  });

  const id = useId();

  const labelId = `${id}-label`;

  const { getFloatingProps } = useInteractions([
    useRole(context, { role: 'dialog' }),
    useDismiss(context, { enabled: dismissable }),
  ]);

  return (
    <>
      <FloatingPortal>
        <Overlay lockScroll>
          <FloatingFocusManager context={context}>
            <Content
              {...getFloatingProps({
                ref: floating,
                'aria-labelledby': labelId,
                className,
                style,
              })}
            >
              <span tabIndex={0} />
              {onClose && <Delete aria-label="close" onClick={onClose} />}
              {headerContent && <Header id={labelId}>{headerContent}</Header>}
              <InnerContent>{children}</InnerContent>
            </Content>
          </FloatingFocusManager>
        </Overlay>
      </FloatingPortal>
    </>
  );
}
